import { useFacilityContext } from '@aspendental/shared-components-web';
import { DateTextInput, Form, SchemaBuilder, TextInput, useHookForm } from '@aspendental/shared-forms-web';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { type FieldValues } from 'react-hook-form';

import NeedsBasedQuestions from '@src/components/NeedsBasedQuestions';
import { REASON_FOR_VISIT_ID } from '@src/components/ReasonForVisit/ReasonForVisit.helpers';
import { DataLayerStepNames } from '@src/constants'; // FALLBACK_FACILITY_PHONE_NUMBER
import { useGeolocation } from '@src/hooks/useGeolocation';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import { EventPatientInformationValueEventTypeEnum } from '@src/lib/__generated/user-events-api';
import GetCoordinates from '@src/services/coordinates';
import { dataLayer } from '@src/services/dataLayer';
import { userEvent } from '@src/services/userEvent';
import {
	PatientInformationSchema,
	PatientInformationSchemaES,
	useAppStore,
	useFormSectionStore,
	useGlobalUXStore,
	useGuarantorInformationFormStore,
	usePatientInformationFormStore,
	useReasonForVisitStore,
} from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
import { ageEligibleForProcedure, guarantorInformationNeeded } from '@src/utils/business-logic';
import { dateInvalid } from '@src/utils/date-parser';
import { unacceptableInputs } from '@src/utils/offensive-words';

const PatientInformationForm = ({ formId }: { formId: string }) => {
	const [collectZipCode, setCollectZipCode] = useState(false);
	const [locationLookupError, setLocationLookupError] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [offensiveInputsFound, setOffensiveInputsFound] = useState(false);
	const router = useRouter();
	const { locale } = router;

	const openDialogWithError = useGlobalUXStore((store) => store.openDialogWithError);

	const firstName = usePatientInformationFormStore((store) => store.firstName);
	const lastName = usePatientInformationFormStore((store) => store.lastName);
	const dateOfBirth = usePatientInformationFormStore((store) => store.dateOfBirth);
	const zipCode = usePatientInformationFormStore((store) => store.zipCode);
	const geoLocation = usePatientInformationFormStore((store) => store.geoLocation);
	const patientAge = usePatientInformationFormStore((store) => store.patientAge);

	const setFirstName = usePatientInformationFormStore((store) => store.setFirstName);
	const setLastName = usePatientInformationFormStore((store) => store.setLastName);
	const setDateOfBirth = usePatientInformationFormStore((store) => store.setDateOfBirth);
	const setZipCode = usePatientInformationFormStore((store) => store.setZipCode);
	const setGeolocation = usePatientInformationFormStore((store) => store.setGeolocation);
	const completeActiveNode = useFormSectionStore((store) => store.completeActiveNode);
	const { t } = useTranslation('patientInformationForm');

	const setGuarantorRequired = useGuarantorInformationFormStore((store) => store.setRequired);

	const setGuarantorInfoNeeded = useFormSectionStore((store) => store.setGuarantorInfoNeeded);
	const { reasonForVisit } = useReasonForVisitStore((store) => store);
	const webFacilityDetails = useFacilityContext();
	const { hasNeedsBasedQuestions, needsBasedAnswers, setNeedsBasedAnswers } = useNeedsBasedQuestionsStore(
		(store) => store
	);

	const { mottoFlow } = useAppStore((store) => store);

	const geolocationService = useGeolocation();

	useEffect(() => {
		if (geoLocation.latitude && geoLocation.longitude) {
			return;
		}
		if (geolocationService.loading) {
			return;
		}

		if (geolocationService.error) {
			return setCollectZipCode(true);
		}

		const { latitude, longitude } = geolocationService;

		if ((geolocationService.loading && typeof latitude !== 'number') || typeof longitude !== 'number') {
			return setCollectZipCode(true);
		}

		setGeolocation(latitude as number, longitude as number);
	}, [geolocationService, setGeolocation, geoLocation]);

	useEffect(() => {
		dataLayer({
			language_preference: locale,
			brand: mottoFlow ? 'mottoaligners' : 'aspendental',
			step_name: DataLayerStepNames.patientInformation,
			reason_for_visit: reasonForVisit,
			reason_for_visit_id: REASON_FOR_VISIT_ID[reasonForVisit],
			...(webFacilityDetails?.officeInfo?.code && {
				website_selected_facility_id: webFacilityDetails?.officeInfo?.code,
			}),
			...(webFacilityDetails?.officeInfo?.name && {
				website_selected_facility_name: webFacilityDetails?.officeInfo?.name,
			}),
		});
	}, [reasonForVisit, webFacilityDetails?.officeInfo?.code, webFacilityDetails?.officeInfo?.name, mottoFlow]);

	// age is determined from the entries in this form, so as soon as age
	// is calculated we set the requirement for guarantor information
	const determineGuarantorInfoNeeded = () => {
		const age = patientAge();

		if (age) {
			const guarantorNeeded = guarantorInformationNeeded(age);
			setGuarantorRequired(guarantorNeeded);
			setGuarantorInfoNeeded(guarantorNeeded);
		} else {
			console.error('patientAge is invalid, cannot determine if gurantor information is neeeded.');
		}
	};

	const runLocationSearch = async ({ zipCode, lastName, firstName, dateOfBirth }) => {
		try {
			const coords = await GetCoordinates(zipCode);
			const coordinates = coords;

			if (typeof coordinates.lat === 'number' && typeof coordinates.lng === 'number') {
				setGeolocation(coordinates.lat, coordinates.lng);
				completeActiveNode();
				logUserEvent({ lastName, firstName, dateOfBirth });
			} else {
				setLocationLookupError(true);
			}
		} catch (e) {
			console.error(e);
			setLocationLookupError(true);
		}
	};

	const logUserEvent = ({ firstName, lastName, dateOfBirth }) => {
		userEvent({
			event_type: EventPatientInformationValueEventTypeEnum.PatientInfo,
			event_data: {
				first_name: firstName,
				last_name: lastName,
				date_of_birth: new Intl.DateTimeFormat('default', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
				}).format(dateOfBirth ?? undefined),
			},
		});
	};

	function isNBQFormValid(data: Record<string, string | string[]> | undefined) {
		for (const key in data) {
			const value = data[key];
			if (Array.isArray(value) && value.length === 0) {
				return false;
			} else if (value === '') {
				return false;
			}
		}
		return true;
	}

	const handleSubmit = async (fields: FieldValues) => {
		setLocationLookupError(false);
		if (hasNeedsBasedQuestions && !isNBQFormValid(needsBasedAnswers)) {
			setHasError(true);
			return;
		} else {
			setHasError(false);
		}
		const { firstName, lastName, dateOfBirth } = fields;

		const unacceptableFields = unacceptableInputs([
			{ name: 'firstName', value: firstName },
			{ name: 'lastName', value: lastName },
		]);

		if (unacceptableFields.length) {
			console.error('offensive inputs detected: ', unacceptableFields.join(', '));
			setOffensiveInputsFound(true);
			return;
		} else {
			setOffensiveInputsFound(false);
		}

		setFirstName(firstName);
		setLastName(lastName);
		setDateOfBirth(dateOfBirth);

		if (fields.zipCode) {
			setZipCode(fields.zipCode);
		}

		determineGuarantorInfoNeeded();

		const useCachedGeolocation = fields.zipCode === zipCode || fields.zipCode === '';
		const age = patientAge();

		const ageOK = ageEligibleForProcedure(age || 0, reasonForVisit as DigitalVisitReasonEnum);

		if (ageOK) {
			useCachedGeolocation
				? completeForm({ lastName, firstName, dateOfBirth })
				: runLocationSearch({ zipCode: fields.zipCode, lastName, firstName, dateOfBirth });
		} else {
			openDialogWithError(t('onlineUnavailable'));
		}
	};

	const completeForm = ({ lastName, firstName, dateOfBirth }) => {
		completeActiveNode();
		logUserEvent({ lastName, firstName, dateOfBirth });
	};

	const englishSchema = locale === 'en-US';

	const schema = SchemaBuilder([
		{
			name: 'firstName',
			validation: englishSchema ? PatientInformationSchema.firstName : PatientInformationSchemaES.firstName,
		},
		{
			name: 'lastName',
			validation: englishSchema ? PatientInformationSchema.lastName : PatientInformationSchemaES.lastName,
		},
		{
			name: 'dateOfBirth',
			validation: englishSchema ? PatientInformationSchema.dateOfBirth : PatientInformationSchemaES.dateOfBirth,
		},
		{
			name: 'zipCode',
			validation: collectZipCode ? PatientInformationSchema.zipCode : PatientInformationSchema.zipCodeOptional,
		},
	]);

	const initialValueDateOfBirth = dateInvalid(dateOfBirth) ? '' : format(dateOfBirth as Date, 'MM/dd/yyyy');

	const defaultValues = {
		firstName,
		lastName,
		dateOfBirth: initialValueDateOfBirth,
		zipCode,
		geoLocation,
	};

	const form = useHookForm({
		schema,
		defaultValues,
	});

	return (
		<Form form={form} onSubmit={handleSubmit} data-test-id="patient-information-form" formId={formId}>
			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<TextInput name="firstName" label={t('firstNameLabel')} data-test-id="patient-info-firstname" />
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextInput name="lastName" label={t('lastNameLabel')} />
				</Grid>
				<Grid item xs={12} sm={6}>
					<DateTextInput label={t('dobLabel')} name="dateOfBirth" autoComplete="bday" required />
				</Grid>
				{collectZipCode && (
					<Grid item sm={12} md={6}>
						<TextInput name="zipCode" label={t('zipCodeLabel')} maxLength={5} />
					</Grid>
				)}
				{offensiveInputsFound && (
					<Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('offensiveInputWarning')}</Typography>
						</Alert>
					</Grid>
				)}
				{locationLookupError && (
					<Grid item xs={12}>
						<Alert severity="error" variant="flatError">
							<Typography color="text.secondary">{t('zipCodeError')}</Typography>
						</Alert>
					</Grid>
				)}
				{hasNeedsBasedQuestions ? (
					<NeedsBasedQuestions
						setNeedsBasedAnswers={setNeedsBasedAnswers}
						needsBasedAnswers={needsBasedAnswers}
						hasError={hasError}
					/>
				) : null}
			</Grid>
		</Form>
	);
};

export default PatientInformationForm;
